import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { sendChatMessage } from '@/services/api';
import { Bot, User, Send } from 'lucide-react';
import { toast } from 'sonner';

const ResidentChat = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      message: 'Hello! I\'m FortiHome AI Assistant. I can help you with society rules, amenity bookings, complaints, billing queries, and more. How can I assist you today?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', message: userMessage }]);
    setLoading(true);

    try {
      const response = await sendChatMessage(userMessage, sessionId);
      setMessages(prev => [...prev, { role: 'assistant', message: response.response }]);
      if (response.session_id) {
        setSessionId(response.session_id);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to send message');
      setMessages(prev => [...prev, { role: 'assistant', message: error.response?.status === 429 ? detail : 'I\'m sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="resident-chat">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>AI Assistant</h1>
        <p className="text-slate-600">Get instant answers to your queries</p>
      </div>

      <Card className="border-slate-200 shadow-xl">
        <CardContent className="p-0">
          {/* Chat Messages */}
          <div className="chat-container overflow-y-auto p-6 space-y-4" style={{ height: '500px' }}>
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex gap-3 message-bubble ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid="chat-message"
              >
                {msg.role === 'assistant' && (
                  <div className="h-8 w-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[70%] px-4 py-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-secondary text-white'
                      : 'bg-slate-100 text-slate-900'
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                </div>
                {msg.role === 'user' && (
                  <div className="h-8 w-8 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-white" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="h-8 w-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="h-5 w-5 text-white" />
                </div>
                <div className="max-w-[70%] px-4 py-3 rounded-2xl bg-slate-100">
                  <div className="flex gap-1">
                    <div className="h-2 w-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="h-2 w-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="h-2 w-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-slate-200 p-4">
            <form onSubmit={handleSend} className="flex gap-2">
              <Input
                placeholder="Type your message..."
                maxLength={1000}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={loading}
                data-testid="chat-input"
                className="flex-1"
              />
              <Button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-secondary hover:bg-secondary/90"
                data-testid="chat-send-btn"
              >
                <Send className="h-5 w-5" />
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResidentChat;